import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useTrack } from "../../hooks/useTrack";
import { supabase } from "../../lib/supabase";
import { getDateRangeBounds, toUTCStartEnd, formatConversionDate as fmtConvDate } from "../../utils/dateUtils";
import { escapeCsv } from "../../utils/formatters";
import { CONVERSIONS_PAGE_SIZE } from "./constants";
import { TIMESTAMP_CUTOFF } from "../../constants/ui";
import { DateRangePicker } from "../../components/DateRangePicker";
import { TemplatesTab } from "./TemplatesTab";
import { CustomerTimelineModal } from "./CustomerTimelineModal";

export function ReportsScreen() {
  const { effectiveAdvertiserId: advertiserId } = useAuth();
  const track = useTrack();
  const [subTab, setSubTab]         = useState("conversions");
  const [sortCol, setSortCol]       = useState("date");
  const [sortDir, setSortDir]       = useState("desc");
  const [filterStatus] = useState("All");
  const [search, setSearch]         = useState("");
  const [expanded, setExpanded]     = useState(null);
  const [timelineCustomer, setTimelineCustomer] = useState(null);
  const [conversionsList, setConversionsList] = useState([]);
  const [conversionsTotalCount, setConversionsTotalCount] = useState(0);
  const [conversionsPage, setConversionsPage] = useState(1);
  const [conversionsLoading, setConversionsLoading] = useState(true);
  const [conversionsError, setConversionsError] = useState(null);
  const [reportsDateRange, setReportsDateRange] = useState("MTD");
  const [reportsAppliedCustomStart, setReportsAppliedCustomStart] = useState(null);
  const [reportsAppliedCustomEnd, setReportsAppliedCustomEnd] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);

  useEffect(() => {
    if (!supabase || advertiserId == null) {
      setConversionsLoading(false);
      setConversionsList([]);
      setConversionsTotalCount(0);
      setConversionsError(advertiserId == null ? "No advertiser linked" : "Supabase not configured");
      return;
    }
    const { startDate, endDate } = getDateRangeBounds(reportsDateRange, reportsAppliedCustomStart, reportsAppliedCustomEnd);
    if (startDate > endDate) {
      setConversionsList([]);
      setConversionsTotalCount(0);
      setConversionsLoading(false);
      setConversionsError(null);
      return;
    }
    const { startISO, endISO } = toUTCStartEnd(startDate, endDate);
    let cancelled = false;
    setConversionsLoading(true);
    setConversionsError(null);
    const from = (conversionsPage - 1) * CONVERSIONS_PAGE_SIZE;
    const to = from + CONVERSIONS_PAGE_SIZE - 1;
    (async () => {
      const { data: rows, error } = await supabase.rpc("get_conversions_list_hybrid", {
        p_advertiser_id: advertiserId,
        p_start_at: startISO,
        p_end_at: endISO,
        p_from: from,
        p_to: to,
      });
      if (cancelled) return;
      if (error) {
        setConversionsList([]);
        setConversionsTotalCount(0);
        setConversionsError(error.message);
        setConversionsLoading(false);
        return;
      }

      const conversionIds = (rows || []).map((r) => r.id);
      const impressionsByConversion = {};
      if (conversionIds.length > 0) {
        const { data: impRows } = await supabase
          .from("assisted_conversion_impressions")
          .select("assisted_conversion_id, asset_name, content_series_name, publisher_name, supply_partner_name, app_name, audio_feed_type, created_at")
          .in("assisted_conversion_id", conversionIds)
          .eq("advertiser_id", advertiserId)
          .order("created_at", { ascending: false });
        if (cancelled) return;
        (impRows || []).forEach((imp) => {
          if (!impressionsByConversion[imp.assisted_conversion_id]) {
            impressionsByConversion[imp.assisted_conversion_id] = imp;
          }
        });
      }

      const list = (rows || []).map((r) => {
        const effectiveTs = new Date(r.created_at) >= new Date(TIMESTAMP_CUTOFF) && r.event_timestamp ? r.event_timestamp : r.created_at;
        const { date, time } = fmtConvDate(effectiveTs);
        const imp = impressionsByConversion[r.id];
        const revenue = r.event_value != null ? Number(r.event_value) : null;
        const spend = r.spend != null ? Number(r.spend) : null;
        const roasVal = revenue != null && spend != null && spend > 0 ? (revenue / spend).toFixed(2) : "—";
        return {
          id: r.event_id || `AC-${r.id}`,
          assistedConversionId: r.id,
          date,
          time,
          customer: r.visitor_id ?? "—",
          type: r.event_type || "Conversion",
          revenue: revenue != null ? `$${revenue.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}` : "—",
          adSpend: spend != null ? `$${spend.toLocaleString("en-US", { minimumFractionDigits:2, maximumFractionDigits:2 })}` : "—",
          creative: imp?.asset_name || "—",
          audioLabel: { 1:"Streaming", 2:"Radio", 3:"Podcast", 7:"Article" }[imp?.audio_feed_type] ?? "Streaming",
          audioSource: imp ? (imp.content_series_name || imp.publisher_name || imp.app_name || "Not Provided").replace(/\b\w/g, c => c.toUpperCase()) : "Not Provided",
          roas: roasVal,
          status: "Confirmed",
          market: [r.region, r.city, r.country].filter(Boolean).join(", ") || "—",
        };
      });
      setConversionsList(list);
      setConversionsTotalCount(rows?.length > 0 ? Number(rows[0].total_count) : 0);
      setConversionsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId, conversionsPage, reportsDateRange, reportsAppliedCustomStart, reportsAppliedCustomEnd]);

  useEffect(() => { setConversionsPage(1); }, [advertiserId, reportsDateRange, reportsAppliedCustomStart, reportsAppliedCustomEnd]);

  useEffect(() => {
    if (!supabase || advertiserId == null) { setSchedulesLoading(false); return; }
    let cancelled = false;
    setSchedulesLoading(true);
    (async () => {
      const { data, error } = await supabase.from("report_schedules").select("*").eq("advertiser_id", advertiserId).order("created_at", { ascending: false });
      if (cancelled) return;
      if (!error) setSchedules(data || []);
      setSchedulesLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const filtered = conversionsList.filter(c => {
    if (filterStatus !== "All" && c.status !== filterStatus) return false;
    if (search && !`${c.id} ${c.customer}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleExportConversions = async () => {
    if (!supabase || advertiserId == null) return;
    const { startDate, endDate } = getDateRangeBounds(reportsDateRange, reportsAppliedCustomStart, reportsAppliedCustomEnd);
    if (startDate > endDate) return;
    setExporting(true);
    const { startISO: p_start, endISO: p_end } = toUTCStartEnd(startDate, endDate);
    const PAGE = 1000;
    let allRows = [];
    let offset = 0;
    while (true) {
      const { data: rows, error } = await supabase.rpc("get_conversions_list_hybrid", {
        p_advertiser_id: advertiserId,
        p_start_at: p_start,
        p_end_at: p_end,
        p_from: offset,
        p_to: offset + PAGE - 1,
      });
      if (error) { setExporting(false); return; }
      const list = rows || [];
      allRows = allRows.concat(list);
      if (list.length < PAGE) break;
      offset += PAGE;
    }
    const headers = ["Order ID","Date","Time","Customer","Type","Revenue","Market"];
    const csvRows = allRows.map((r) => {
      const effectiveTs = new Date(r.created_at) >= new Date(TIMESTAMP_CUTOFF) && r.event_timestamp ? r.event_timestamp : r.created_at;
      const { date, time } = fmtConvDate(effectiveTs);
      const revenue = r.event_value != null ? Number(r.event_value).toFixed(2) : "";
      const market = [r.region, r.city, r.country].filter(Boolean).join(", ") || "";
      return [r.event_id || `AC-${r.id}`, date, time, r.visitor_id ?? "", r.event_type || "Conversion", revenue, market].map(escapeCsv).join(",");
    });
    const csv = [headers.map(escapeCsv).join(","), ...csvRows].join("\r\n");
    const blob = new Blob([csv], { type:"text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversions-${startDate}-to-${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    track('conversions_exported', { date_range: reportsDateRange, row_count: allRows.length });
    setExporting(false);
  };

  const SortIcon = ({ col }) => (
    <span style={{ color: sortCol===col ? "#F90091" : "#B8B8B8", marginLeft:4, fontSize:10 }}>
      {sortCol===col ? (sortDir==="asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  return (
    <>
    <div style={{ padding:"28px 28px", maxWidth:1000, margin:"0 auto" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:22 }}>
        <div>
          <h2 style={{ fontSize:28, fontWeight:600, color:"#151515", marginBottom:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Reports</h2>
        </div>
        <DateRangePicker
          dateRange={reportsDateRange}
          setDateRange={setReportsDateRange}
          appliedCustomStart={reportsAppliedCustomStart}
          setAppliedCustomStart={setReportsAppliedCustomStart}
          appliedCustomEnd={reportsAppliedCustomEnd}
          setAppliedCustomEnd={setReportsAppliedCustomEnd}
        />
      </div>

      <div style={{ display:"flex", gap:0, borderBottom:"1px solid #E2E2E2", marginBottom:26 }}>
        {[{ id:"conversions", label:"Conversions" }, { id:"templates", label:"Templates" }].map(t => (
          <button key={t.id} onClick={() => { setSubTab(t.id); track('reports_subtab_viewed', { subtab: t.id }); }} style={{
            padding:"11px 22px", background:"transparent", border:"none",
            borderBottom: subTab===t.id ? "2px solid #F90091" : "2px solid transparent",
            color: subTab===t.id ? "#151515" : "#7b7b7b",
            fontSize:13, fontWeight: subTab===t.id ? 600 : 400, cursor:"pointer", marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>

      {subTab === "conversions" && <>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:12 }}>
          <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, padding:"7px 12px", display:"flex", alignItems:"center", gap:8, boxShadow:"0 2px 6px rgba(0,0,0,0.06)" }}>
            <span style={{ color:"#7b7b7b", fontSize:13 }}>🔍</span>
            <input
              placeholder="Search order, customer..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:12, width:220 }}
            />
          </div>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <button onClick={handleExportConversions} disabled={exporting} style={{ padding:"8px 14px", background:"#FFFFFF", color: exporting ? "#B8B8B8" : "#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, cursor: exporting ? "default" : "pointer" }}>{exporting ? "Exporting…" : "📤 Export"}</button>
            <button style={{ padding:"8px 14px", background:"#FFFFFF", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, cursor:"pointer" }}>🔗 Share</button>
          </div>
        </div>

        <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:14, overflow:"hidden", marginBottom:14, boxShadow:"0 3px 15px rgba(0,0,0,0.06)" }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:10, padding:"12px 18px", borderBottom:"1px solid #F0F0F0", background:"#FAFAFA" }}>
            {[
              { key:"id",       label:"Order ID" },
              { key:"date",     label:"Date" },
              { key:"customer", label:"Customer" },
              { key:"type",     label:"Type" },
              { key:"revenue",  label:"Revenue" },
            ].map(h => (
              <div key={h.key} onClick={() => toggleSort(h.key)}
                style={{ fontSize:10, color: sortCol===h.key ? "#F90091" : "#7b7b7b", textTransform:"uppercase", letterSpacing:1, cursor:"pointer", userSelect:"none", display:"flex", alignItems:"center", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {h.label}<SortIcon col={h.key} />
              </div>
            ))}
          </div>

          {conversionsLoading && (
            <div style={{ padding:"40px 0", textAlign:"center", color:"#7b7b7b", fontSize:13 }}>Loading conversions…</div>
          )}
          {!conversionsLoading && conversionsError && (
            <div style={{ padding:"40px 0", textAlign:"center", color:"#e53935", fontSize:13 }}>{conversionsError}</div>
          )}
          {!conversionsLoading && !conversionsError && filtered.length === 0 && (
            <div style={{ padding:"40px 0", textAlign:"center", color:"#7b7b7b", fontSize:13 }}>No conversions match your filters.</div>
          )}
          {!conversionsLoading && !conversionsError && filtered.map(c => (
            <React.Fragment key={c.id}>
              <div
                onClick={() => {
                  const expanding = expanded !== c.id;
                  setExpanded(expanding ? c.id : null);
                  if (expanding) track('conversion_expanded', { conversion_id: c.id });
                }}
                style={{
                  display:"grid", gridTemplateColumns:"repeat(5, 1fr)",
                  gap:10, padding:"13px 18px",
                  borderBottom:"1px solid #F5F5F5",
                  background: expanded===c.id ? "#FFF5FB" : "transparent",
                  cursor:"pointer",
                  transition:"background 0.15s",
                }}>
                <div style={{ color:"#151515", fontSize:12, fontWeight:600, fontFamily:"monospace", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.id}</div>
                <div style={{ fontSize:12, color:"#151515", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.date} · {c.time}</div>
                <div style={{ minWidth:0, overflow:"hidden" }}>
                  <div style={{ fontSize:12, color:"#151515", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.customer}</div>
                </div>
                <div style={{ minWidth:0, overflow:"hidden" }}>
                  <span style={{ fontSize:11, padding:"2px 8px", borderRadius:20, color:"#151515", fontWeight:600, display:"inline-block", maxWidth:"100%", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {c.type}
                  </span>
                </div>
                <div style={{ fontSize:12, color:"#151515", fontWeight: c.revenue==="—" ? 400 : 600, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.revenue}</div>
              </div>

              {expanded===c.id && (
                <div key={c.id+"_exp"} style={{ background:"#FAFAFA", borderBottom:"1px solid #F0F0F0", padding:"16px 24px" }}>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:16, marginBottom:14 }}>
                    {[
                      { label:"Order ID",    value:c.id },
                      { label:"Date & Time", value:`${c.date} · ${c.time}` },
                      { label:"Market / Geo",value:c.market },
                      { label:"Ad Creative", value:c.creative },
                      { label:c.audioLabel,  value:c.audioSource },
                    ].map(d => (
                      <div key={d.label}>
                        <div style={{ fontSize:10, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{d.label}</div>
                        <div style={{ fontSize:13, color:"#151515" }}>{d.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, paddingTop:12, borderTop:"1px solid #E2E2E2" }}>
                    {[
                      { label:"Customer",        value:c.customer },
                      { label:"Conversion Type", value:c.type },
                      { label:"Revenue",         value:c.revenue },
                      { label:"ROAS",            value:c.roas },
                    ].map(d => (
                      <div key={d.label}>
                        <div style={{ fontSize:10, color:"#7b7b7b", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{d.label}</div>
                        <div style={{ fontSize:13, color:"#151515" }}>{d.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop:14, display:"flex", gap:10 }}>
                    <button onClick={e => { e.stopPropagation(); setTimelineCustomer(c); track('customer_timeline_opened', { conversion_id: c.id }); }} style={{ padding:"7px 18px", background:"#F90091", color:"#fff", border:"none", borderRadius:7, fontSize:12, fontWeight:600, cursor:"pointer" }}>View Customer Timeline →</button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
          <div style={{ fontSize:12, color:"#7b7b7b" }}>
            Showing {conversionsTotalCount === 0 ? "0" : `${(conversionsPage - 1) * CONVERSIONS_PAGE_SIZE + 1}–${Math.min(conversionsPage * CONVERSIONS_PAGE_SIZE, conversionsTotalCount)}`} of {conversionsTotalCount} conversions
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button
              disabled={conversionsPage <= 1}
              onClick={() => setConversionsPage((p) => Math.max(1, p - 1))}
              style={{ padding:"6px 14px", background: conversionsPage <= 1 ? "#F5F5F5" : "#FFFFFF", color: conversionsPage <= 1 ? "#B8B8B8" : "#626262", border:"1px solid #E2E2E2", borderRadius:7, fontSize:12, cursor: conversionsPage <= 1 ? "default" : "pointer" }}
            >← Prev</button>
            <button
              disabled={conversionsPage * CONVERSIONS_PAGE_SIZE >= conversionsTotalCount}
              onClick={() => setConversionsPage((p) => p + 1)}
              style={{ padding:"6px 14px", background: conversionsPage * CONVERSIONS_PAGE_SIZE >= conversionsTotalCount ? "#F5F5F5" : "#FFFFFF", color: conversionsPage * CONVERSIONS_PAGE_SIZE >= conversionsTotalCount ? "#B8B8B8" : "#626262", border:"1px solid #E2E2E2", borderRadius:7, fontSize:12, cursor: conversionsPage * CONVERSIONS_PAGE_SIZE >= conversionsTotalCount ? "default" : "pointer" }}
            >Next →</button>
          </div>
        </div>
      </>}

      {subTab === "insights" && <>
        <div style={{ position:"relative" }}>
          <div style={{ filter:"grayscale(0.3)", opacity:0.5, pointerEvents:"none" }}>
            <div style={{ marginBottom:20 }}>
              <h2 style={{ fontSize:20, fontWeight:600, color:"#151515", marginBottom:0 }}>Audience Insights</h2>
            </div>
            <div style={{ display:"grid", gap:12, marginBottom:24 }}>
              {[
                { icon:"🕗", title:"Prime listening window",         body:"Your best converters tune in on weekday mornings, 7–9am. Impressions are weighted heavily in this window." },
                { icon:"👨‍👩‍👧", title:"Households with kids 3× better", body:"Audiences with children in the home convert at 3.1× the rate of the general population." },
                { icon:"📍", title:"Top 3 markets",                  body:"New York, Chicago, and Austin over-index on ROAS by 40%+ vs. other geos." },
                { icon:"🎙️", title:"True crime & news podcast fans",  body:"This genre cluster converts 2.2× better than your baseline. Heavy weight is given here." },
              ].map(ins => (
                <div key={ins.title} style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:12, padding:18, display:"flex", gap:14, alignItems:"flex-start", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
                  <span style={{ fontSize:24 }}>{ins.icon}</span>
                  <div>
                    <div style={{ fontWeight:600, color:"#151515", fontSize:14, marginBottom:4 }}>{ins.title}</div>
                    <div style={{ color:"#626262", fontSize:13, lineHeight:1.5 }}>{ins.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:14, padding:22, marginBottom:20, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontWeight:600, color:"#151515", marginBottom:18, fontSize:14 }}>Conversion Rate by Daypart</div>
              {[
                { label:"Morning 6–10am",   val:88 },
                { label:"Midday 10am–2pm",  val:52 },
                { label:"Afternoon 2–6pm",  val:44 },
                { label:"Evening 6–10pm",   val:61 },
                { label:"Late Night 10pm+", val:29 },
              ].map(r => (
                <div key={r.label} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                  <div style={{ width:130, fontSize:12, color:"#626262", flexShrink:0 }}>{r.label}</div>
                  <div style={{ flex:1, background:"#F0F0F0", borderRadius:4, height:18, overflow:"hidden" }}>
                    <div style={{ width:`${r.val}%`, height:"100%", background: r.val>70 ? "#F90091" : "#E2E2E2", borderRadius:4 }} />
                  </div>
                  <div style={{ width:32, fontSize:12, color: r.val>70 ? "#F90091" : "#7b7b7b", textAlign:"right" }}>{r.val}%</div>
                </div>
              ))}
            </div>
            <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:14, padding:22, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontWeight:600, color:"#151515", marginBottom:18, fontSize:14 }}>Conversions by Podcast Genre</div>
              {[
                { label:"True Crime",       val:92, count:"54 conv." },
                { label:"News & Politics",  val:74, count:"38 conv." },
                { label:"Business",         val:58, count:"26 conv." },
                { label:"Comedy",           val:41, count:"16 conv." },
                { label:"Health & Wellness",val:28, count:"8 conv."  },
              ].map(r => (
                <div key={r.label} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                  <div style={{ width:130, fontSize:12, color:"#626262", flexShrink:0 }}>{r.label}</div>
                  <div style={{ flex:1, background:"#F0F0F0", borderRadius:4, height:18, overflow:"hidden" }}>
                    <div style={{ width:`${r.val}%`, height:"100%", background: r.val>80 ? "#F90091" : r.val>60 ? "#323084" : "#E2E2E2", borderRadius:4 }} />
                  </div>
                  <div style={{ width:60, fontSize:11, color:"#7b7b7b", textAlign:"right" }}>{r.count}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ position:"absolute", inset:0, background:"rgba(255,255,255,0.85)", display:"flex", alignItems:"center", justifyContent:"center", borderRadius:14 }}>
            <span style={{ fontSize:20, fontWeight:600, color:"#7b7b7b", letterSpacing:"0.5px" }}>Coming soon</span>
          </div>
        </div>
      </>}

      {subTab === "templates" && (
        <TemplatesTab
          dateRange={reportsDateRange}
          appliedCustomStart={reportsAppliedCustomStart}
          appliedCustomEnd={reportsAppliedCustomEnd}
          schedules={schedules}
          schedulesLoading={schedulesLoading}
          setSchedules={setSchedules}
        />
      )}
    </div>

    {timelineCustomer && <CustomerTimelineModal customer={timelineCustomer} onClose={() => setTimelineCustomer(null)} />}
    </>
  );
}
